import { bookingResourceIds } from '@/lib/autoAdjustGroup';
import { isDemandBooking } from '@/lib/demand-booking';
import type { Booking } from '@/types';
import { isUnallocatedBooking } from '@/utils/unallocated-booking';

/** The shape of a Bryntum event record, as far as selection needs it. */
export interface SelectedEventLike {
  id?: unknown;
  data?: {
    id?: unknown;
    isAbsenceEvent?: boolean;
    isHeatmapEvent?: boolean;
    isWhatIfGhost?: boolean;
    originalBooking?: Booking;
  };
}

interface ResolveOptions {
  findBooking: (eventId: string) => Booking | undefined;
  /** Ids of occurrences mid-deletion — they must not be deleted again. */
  isLocked?: (bookingId: string) => boolean;
  /** Bookings of a type the user may not change. */
  isRestricted?: (booking: Booking) => boolean;
}

/** Gets the base booking id from a timeline event id. */
export function baseBookingIdFromEventId(eventId: string): string {
  return eventId.split('__mr__')[0];
}

/** Checks whether an event id belongs to a booking or one of its sibling tiles. */
export function isSiblingEventId(eventId: string, baseBookingId: string): boolean {
  return eventId === baseBookingId || eventId.startsWith(`${baseBookingId}__mr__`);
}

/**
 * The bookings a set of selected timeline events stands for.
 *
 * Only booking tiles count: absence, heatmap and what-if ghost events share the
 * event store but have no booking behind them. A multi-resource booking renders
 * one tile per resource row, so the same booking can arrive several times and is
 * de-duplicated on its own id.
 */
export function bookingsFromSelectedEvents(
  selected: readonly SelectedEventLike[],
  { findBooking, isLocked, isRestricted }: ResolveOptions,
): Booking[] {
  const bookings: Booking[] = [];
  const seen = new Set<string>();

  for (const record of selected) {
    const data = record?.data;
    if (data?.isAbsenceEvent || data?.isHeatmapEvent || data?.isWhatIfGhost) continue;

    const rawId = record?.id ?? data?.id;
    if (rawId == null) continue;

    const booking = findBooking(String(rawId)) ?? data?.originalBooking;
    if (!booking) continue;

    const bookingId = String(booking.id);
    if (seen.has(bookingId)) continue;
    if (isLocked?.(bookingId)) continue;
    if (isRestricted?.(booking)) continue;

    seen.add(bookingId);
    bookings.push(booking);
  }

  return bookings;
}

/**
 * The events that belong with the selected ones and are not selected yet: the tiles a
 * multi-resource booking renders on the other resource rows, and the pieces a booking an
 * auto-adjust type broke up around a non-working day, a leave day or a public holiday was
 * saved as. Both read as one booking on the timeline, so selecting any part of one selects
 * all of it and a bulk action then covers the whole booking.
 */
export function linkedEventIds(
  selectedEventIds: readonly string[],
  allEventIds: readonly string[],
  findBooking: (bookingId: string) => Booking | undefined,
): string[] {
  const selected = new Set(selectedEventIds.map(String));
  const multiResourceBases = new Set<string>();
  const groupIds = new Set<string>();

  for (const eventId of selected) {
    const booking = findBooking(baseBookingIdFromEventId(eventId));
    if (!booking) continue;
    if ((booking.resourceIds?.length ?? 0) > 1) {
      multiResourceBases.add(String(booking.id));
    }
    if (booking.autoAdjustGroupId) groupIds.add(String(booking.autoAdjustGroupId));
  }

  const linked: string[] = [];

  for (const rawEventId of allEventIds) {
    const eventId = String(rawEventId);
    if (selected.has(eventId)) continue;

    if ([...multiResourceBases].some(baseId => isSiblingEventId(eventId, baseId))) {
      linked.push(eventId);
      continue;
    }

    const groupId = findBooking(baseBookingIdFromEventId(eventId))?.autoAdjustGroupId;
    if (groupId && groupIds.has(String(groupId))) linked.push(eventId);
  }

  return linked;
}

/**
 * What a bulk action on `clicked` should target.
 *
 * A right-click inside a multi-selection acts on the whole selection; a
 * right-click on anything else acts on that booking alone, leaving the
 * selection untouched. This is shared by bulk delete and status changes.
 */
export function bulkActionTargets(clicked: Booking, selection: readonly Booking[]): Booking[] {
  const clickedId = String(clicked.id);
  const isInSelection = selection.some(booking => String(booking.id) === clickedId);
  return selection.length > 1 && isInSelection ? [...selection] : [clicked];
}

/**
 * Whether the Move Booking dialog may be opened for the booking. The dialog moves a
 * booking to one resource, which a booking held by several of them cannot be reduced to,
 * so it is offered only for a booking on a single resource. Open Demand bookings hold no
 * resource to move from, and bookings created by accepting a demand plan stay tied to the
 * demand's allocation, so both are resourced through the Open Demand plan flow instead.
 */
export function canMoveBooking(
  booking: Booking,
  isBulk = false,
  all: readonly Booking[] = [booking],
): boolean {
  return !isBulk
    && !booking.recurrenceGroupId
    && !isUnallocatedBooking(booking)
    && !isDemandBooking(booking)
    && bookingResourceIds(booking, all).length <= 1;
}

/**
 * Whether the booking may be copied. A paste recreates the booking on the resource
 * of the row it lands on, which cannot stand for a booking held by several of them,
 * so copy is offered only for a booking on a single resource. Open Demand bookings are
 * not copied either — a paste would resource demand that is meant to stay open, and a
 * paste of a demand-plan booking would add hours the demand never asked for.
 */
export function canCopyBooking(
  booking: Booking,
  isBulk = false,
  all: readonly Booking[] = [booking],
): boolean {
  return !isBulk
    && !booking.recurrenceGroupId
    && !isUnallocatedBooking(booking)
    && !isDemandBooking(booking)
    && bookingResourceIds(booking, all).length <= 1;
}
